import React, { useState, useEffect, useRef } from 'react';
import { getLogs, clearLogs } from '../services/api';
import './ViewLogs.css';

const ViewLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(2000); // 2 secondes par défaut
  const logsEndRef = useRef(null);

  const fetchLogs = async () => {
    try {
      const response = await getLogs(200);
      setLogs(response.data.logs || []);
      setError('');
    } catch (err) {
      console.error('Erreur lors de la récupération des logs:', err);
      setError('Impossible de charger les logs. Vérifiez que le serveur est démarré.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchLogs();
      }, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  useEffect(() => {
    // Auto-scroll vers le bas quand de nouveaux logs arrivent
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const handleClearLogs = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vider tous les logs ?')) {
      try {
        await clearLogs();
        setLogs([]);
        alert('Logs vidés avec succès.');
      } catch (err) {
        console.error('Erreur lors du vidage des logs:', err);
        alert('Erreur lors du vidage des logs.');
      }
    }
  };

  const getLogLevelClass = (level) => {
    switch (level) {
      case 'error':
        return 'log-error';
      case 'warning':
        return 'log-warning';
      case 'info':
        return 'log-info';
      case 'debug':
        return 'log-debug';
      default:
        return 'log-default';
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3
      });
    } catch (e) {
      return timestamp;
    }
  };

  return (
    <div className="view-logs-container">
      <div className="view-logs-header">
        <h1>📋 Logs du Serveur</h1>
        <div className="view-logs-controls">
          <button 
            onClick={fetchLogs} 
            className="btn-refresh"
            disabled={loading}
          >
            🔄 Actualiser
          </button>
          <button 
            onClick={handleClearLogs} 
            className="btn-clear"
          >
            🗑️ Vider les logs
          </button>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-actualisation
          </label>
          {autoRefresh && (
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="select-interval"
            >
              <option value={1000}>1 seconde</option>
              <option value={2000}>2 secondes</option>
              <option value={5000}>5 secondes</option>
              <option value={10000}>10 secondes</option>
            </select>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <div className="logs-info">
        <span>Total: {logs.length} logs</span>
        {autoRefresh && <span className="auto-refresh-indicator">🔄 Auto-actualisation active</span>}
      </div>

      <div className="logs-container">
        {loading && logs.length === 0 ? (
          <div className="loading">Chargement des logs...</div>
        ) : logs.length === 0 ? (
          <div className="no-logs">Aucun log disponible</div>
        ) : (
          logs.map((log, index) => (
            <div key={index} className={`log-entry ${getLogLevelClass(log.level)}`}>
              <div className="log-header">
                <span className="log-timestamp">{formatTimestamp(log.timestamp)}</span>
                <span className={`log-level log-level-${log.level}`}>
                  {log.level.toUpperCase()}
                </span>
              </div>
              <div className="log-message">{log.message}</div>
              {log.data && (
                <details className="log-data">
                  <summary>Données détaillées</summary>
                  <pre>{log.data}</pre>
                </details>
              )}
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default ViewLogs;

